import React, { useEffect, useState } from "react";
import { API_BASE_URL } from "../utils/config";
import CustomSelect from "../components/CustomSelect";
import PasswordInput from "../components/PasswordInput";
import AvatarViewer from "../components/AvatarViewer";
import TeamBadgeList from "../components/TeamBadgeList";
import { ROLE_CLASS, ROLE_LABEL } from "../utils/roleLabels";

// Roles an ACC can hand out. Not "superadmin": that stays a deliberate, separate
// step from the role badge in the table, never a side effect of approving a sign-up.
const APPROVE_ROLE_OPTIONS = ["user", "admin", "sekretaris", "bendahara"].map((value) => ({
  value,
  label: ROLE_LABEL[value],
}));

function StatusDot({ active }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`status-dot ${active ? "status-active" : "status-inactive"}`} />
      <span className="text-[13px]" style={{ color: active ? "var(--success)" : "var(--text-secondary)" }}>
        {active ? "Active" : "Inactive"}
      </span>
    </span>
  );
}

function Users() {
  const [users, setUsers]         = useState([]);
  const [me, setMe]               = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [createSuccess, setCreateSuccess] = useState("");

  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole]         = useState("user");

  const [search, setSearch] = useState("");

  // Pendaftaran lewat Google yang menunggu ACC
  const [approveRoles, setApproveRoles] = useState({}); // user id -> role picked in the ACC dropdown
  const [busyId, setBusyId]             = useState(null);
  const [accNotice, setAccNotice]       = useState(null); // { type: "success" | "warning", text }

  const [showResetModal, setShowResetModal]         = useState(false);
  const [resetUser, setResetUser]                   = useState(null);
  const [resetPassword, setResetPassword]           = useState("");
  const [resetConfirmPassword, setResetConfirmPassword] = useState("");
  const [resetError, setResetError]                 = useState("");
  const [resetLoading, setResetLoading]             = useState(false);

  const token = localStorage.getItem("accessToken");
  const authHeaders = token
    ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };

  useEffect(() => {
    if (!token) {
      setError("No token found. Please login again.");
      setLoading(false);
      return;
    }
    fetch(`${API_BASE_URL}/api/auth/me/`, { headers: authHeaders })
      .then((r) => (r.ok ? r.json() : null))
      .then(setMe)
      .catch(() => setMe(null));

    setLoading(true);
    fetch(`${API_BASE_URL}/api/users/`, { headers: authHeaders })
      .then(async (r) => {
        if (!r.ok) {
          const d = await r.json().catch(() => null);
          throw new Error(d?.detail || "Failed to load users.");
        }
        return r.json();
      })
      .then((d) => { setUsers(Array.isArray(d) ? d : []); setLoading(false); })
      .catch((err) => { setError(err.message); setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateUser = (e) => {
    e.preventDefault();
    setError(""); setCreateSuccess("");
    if (!newUsername.trim() || !newPassword.trim()) { setError("Username and password are required."); return; }
    fetch(`${API_BASE_URL}/api/users/`, {
      method: "POST", headers: authHeaders,
      body: JSON.stringify({ username: newUsername.trim(), password: newPassword.trim(), role: newRole, is_active: true }),
    })
      .then(async (r) => {
        if (!r.ok) {
          const d = await r.json().catch(() => null);
          let msg = "Failed to create user.";
          if (d && typeof d === "object") {
            const k = Object.keys(d)[0];
            msg = Array.isArray(d[k]) ? d[k][0] : (d.detail || msg);
          }
          throw new Error(msg);
        }
        return r.json();
      })
      .then((created) => {
        setUsers((p) => [...p, created]);
        setNewUsername(""); setNewPassword(""); setNewRole("user");
        setCreateSuccess("User created successfully.");
      })
      .catch((err) => setError(err.message));
  };

  const handleChangeRole = (userId, newRoleValue) => {
    fetch(`${API_BASE_URL}/api/users/${userId}/`, {
      method: "PUT", headers: authHeaders,
      body: JSON.stringify({ role: newRoleValue }),
    })
      .then(async (r) => {
        if (!r.ok) { const d = await r.json().catch(() => null); throw new Error(d?.detail || "Failed to update role."); }
        return r.json();
      })
      .then((updated) => setUsers((p) => p.map((u) => (u.id === updated.id ? updated : u))))
      .catch((err) => alert(err.message));
  };

  const openResetModal = (user) => {
    setResetUser(user); setResetPassword(""); setResetConfirmPassword(""); setResetError(""); setShowResetModal(true);
  };
  const closeResetModal = () => {
    setShowResetModal(false); setResetUser(null); setResetPassword(""); setResetConfirmPassword(""); setResetError(""); setResetLoading(false);
  };

  const handleResetPasswordSubmit = (e) => {
    e.preventDefault();
    if (!resetUser) return;
    setResetError("");
    if (!resetPassword.trim() || !resetConfirmPassword.trim()) { setResetError("Please fill in both password fields."); return; }
    if (resetPassword.trim() !== resetConfirmPassword.trim()) { setResetError("Passwords do not match."); return; }
    setResetLoading(true);
    fetch(`${API_BASE_URL}/api/users/${resetUser.id}/`, {
      method: "PUT", headers: authHeaders,
      body: JSON.stringify({ password: resetPassword.trim() }),
    })
      .then(async (r) => {
        if (!r.ok) { const d = await r.json().catch(() => null); throw new Error(d?.detail || "Failed to reset password."); }
        return r.json();
      })
      .then(() => { setResetLoading(false); closeResetModal(); })
      .catch((err) => { setResetLoading(false); setResetError(err.message); });
  };

  const handleToggleActive = (user) => {
    const activating = !user.is_active;
    const confirmMsg = activating
      ? `Aktifkan kembali akun "${user.username}"?`
      : `Tangguhkan akun "${user.username}"? User ini tidak akan bisa login sampai diaktifkan kembali.`;
    if (!window.confirm(confirmMsg)) return;
    fetch(`${API_BASE_URL}/api/users/${user.id}/`, {
      method: "PUT", headers: authHeaders,
      body: JSON.stringify({ is_active: activating }),
    })
      .then(async (r) => {
        if (!r.ok) { const d = await r.json().catch(() => null); throw new Error(d?.detail || "Failed to update status."); }
        return r.json();
      })
      .then((updated) => setUsers((p) => p.map((u) => (u.id === updated.id ? updated : u))))
      .catch((err) => alert(err.message));
  };

  const handleDeleteUser = (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    fetch(`${API_BASE_URL}/api/users/${userId}/`, { method: "DELETE", headers: authHeaders })
      .then((r) => {
        if (!r.ok && r.status !== 204) throw new Error("Failed to delete user.");
        setUsers((p) => p.filter((u) => u.id !== userId));
      })
      .catch((err) => alert(err.message));
  };

  const handleApprove = (user) => {
    const role = approveRoles[user.id] || user.requested_role || "user";
    setBusyId(user.id);
    setAccNotice(null);
    fetch(`${API_BASE_URL}/api/users/${user.id}/approve-registration/`, {
      method: "POST", headers: authHeaders,
      body: JSON.stringify({ role }),
    })
      .then(async (r) => {
        const d = await r.json().catch(() => null);
        if (!r.ok) throw new Error(d?.detail || "Gagal meng-ACC pendaftaran.");
        return d;
      })
      .then(({ warning, ...updated }) => {
        setUsers((p) => p.map((u) => (u.id === updated.id ? updated : u)));
        const done = `${user.username} di-ACC sebagai ${ROLE_LABEL[role]}.`;
        setAccNotice(warning ? { type: "warning", text: `${done} ${warning}` } : { type: "success", text: done });
      })
      .catch((err) => alert(err.message))
      .finally(() => setBusyId(null));
  };

  const handleReject = (user) => {
    if (!window.confirm(
      `Tolak pendaftaran "${user.username}" (${user.google_email})? Akunnya tetap tersimpan sebagai "Ditolak" dan nggak bisa masuk. Hapus akunnya kalau dia perlu bisa daftar ulang.`
    )) return;
    setBusyId(user.id);
    setAccNotice(null);
    fetch(`${API_BASE_URL}/api/users/${user.id}/reject-registration/`, {
      method: "POST", headers: authHeaders,
    })
      .then(async (r) => {
        const d = await r.json().catch(() => null);
        if (!r.ok) throw new Error(d?.detail || "Gagal menolak pendaftaran.");
        return d;
      })
      .then((updated) => setUsers((p) => p.map((u) => (u.id === updated.id ? updated : u))))
      .catch((err) => alert(err.message))
      .finally(() => setBusyId(null));
  };

  const handleUnlinkGoogle = (user) => {
    if (!window.confirm(
      `Lepas ikatan Google dari "${user.username}"? Dia nggak bisa masuk lewat Google lagi, tapi tetap bisa masuk dengan username dan password.`
    )) return;
    fetch(`${API_BASE_URL}/api/users/${user.id}/google/`, { method: "DELETE", headers: authHeaders })
      .then(async (r) => {
        const d = await r.json().catch(() => null);
        if (!r.ok) throw new Error(d?.detail || "Gagal melepas ikatan Google.");
        return d;
      })
      .then((updated) => setUsers((p) => p.map((u) => (u.id === updated.id ? updated : u))))
      .catch((err) => alert(err.message));
  };

  // Pending sign-ups get their own panel and stay out of the main table, so
  // nobody can hand them a role from the table's role badge and skip the ACC.
  const pendingUsers = users.filter((u) => u.registration_status === "pending");
  const regularUsers = users.filter((u) => u.registration_status !== "pending");

  const term = search.trim().toLowerCase();
  const filteredUsers = term
    ? regularUsers.filter((u) => {
        const roleLabel = ROLE_LABEL[u.role] || u.role || "";
        const teamNames = (u.teams || []).map((t) => t.name).join(" ");
        return (
          u.username.toLowerCase().includes(term) ||
          roleLabel.toLowerCase().includes(term) ||
          teamNames.toLowerCase().includes(term) ||
          (u.google_email || "").toLowerCase().includes(term)
        );
      })
    : regularUsers;

  return (
    <div className="space-y-6">
      {/* ── Page heading ── */}
      <div>
        <h1 className="text-[28px] font-bold text-gradient leading-none">Users</h1>
        <p className="text-[13px] mt-1" style={{ color: "var(--text-secondary)" }}>
          Manage accounts and access levels
        </p>
      </div>

      {/* ── Global feedback ── */}
      {error && (
        <div className="rounded-xl px-4 py-3 text-[13px] border animate-fade-in"
          style={{ color: "var(--danger)", background: "var(--danger-subtle)", borderColor: "color-mix(in srgb, var(--danger) 30%, transparent)" }}>
          {error}
        </div>
      )}
      {createSuccess && (
        <div className="rounded-xl px-4 py-3 text-[13px] border animate-fade-in"
          style={{ color: "var(--success)", background: "var(--success-subtle)", borderColor: "color-mix(in srgb, var(--success) 30%, transparent)" }}>
          {createSuccess}
        </div>
      )}

      {accNotice && (
        <div className="rounded-xl px-4 py-3 text-[13px] border animate-fade-in"
          style={accNotice.type === "warning"
            ? { color: "var(--amber)", background: "var(--amber-subtle)", borderColor: "color-mix(in srgb, var(--amber) 30%, transparent)" }
            : { color: "var(--success)", background: "var(--success-subtle)", borderColor: "color-mix(in srgb, var(--success) 30%, transparent)" }}>
          {accNotice.text}
        </div>
      )}

      {/* ── Menunggu ACC (daftar lewat Google) ── */}
      {pendingUsers.length > 0 && (
        <div className="glass-panel p-5">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-[15px] font-semibold" style={{ color: "var(--text-primary)" }}>
              Menunggu ACC
            </h2>
            <span className="badge badge-warning">{pendingUsers.length}</span>
          </div>
          <p className="text-[12px] mb-4" style={{ color: "var(--text-secondary)" }}>
            Daftar lewat Google. Mereka belum bisa masuk sampai kamu ACC.
          </p>
          <div className="space-y-3">
            {pendingUsers.map((u) => (
              <div key={u.id}
                className="flex flex-col gap-3 lg:flex-row lg:items-center rounded-xl p-3 border"
                style={{ borderColor: "var(--border)" }}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-[13px]" style={{ color: "var(--text-primary)" }}>
                    {u.username}
                  </div>
                  <div className="text-[12px] break-all" style={{ color: "var(--text-secondary)" }}>
                    {u.google_email}
                  </div>
                  <div className="mt-1.5 flex items-center gap-2 text-[11px]" style={{ color: "var(--text-secondary)" }}>
                    Minta role
                    <span className={`role-badge ${ROLE_CLASS[u.requested_role] || "role-user"}`}>
                      {ROLE_LABEL[u.requested_role] || u.requested_role || "-"}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="w-40">
                    <CustomSelect
                      value={approveRoles[u.id] || u.requested_role || "user"}
                      onChange={(v) => setApproveRoles((p) => ({ ...p, [u.id]: v }))}
                      options={APPROVE_ROLE_OPTIONS}
                    />
                  </div>
                  <button
                    onClick={() => handleApprove(u)}
                    disabled={busyId === u.id}
                    className="btn-primary btn-sm"
                  >
                    {busyId === u.id ? "Memproses…" : "ACC"}
                  </button>
                  <button
                    onClick={() => handleReject(u)}
                    disabled={busyId === u.id}
                    className="btn-danger btn-sm"
                  >
                    Tolak
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Create user form card ── */}
      <div className="glass-panel p-5">
        <h2 className="text-[15px] font-semibold mb-4" style={{ color: "var(--text-primary)" }}>
          Create New User
        </h2>
        <form onSubmit={handleCreateUser} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:items-end">
          <div>
            <label className="block text-[11px] font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>
              Username *
            </label>
            <input
              type="text"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              className="glass-input"
              placeholder="e.g. sutra"
              required
            />
          </div>
          <div>
            <label className="block text-[11px] font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>
              Password *
            </label>
            <PasswordInput
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="glass-input"
              placeholder="••••••••"
              required
            />
          </div>
          <div>
            <label className="block text-[11px] font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>
              Role *
            </label>
            <CustomSelect
              value={newRole}
              onChange={setNewRole}
              options={[
                { value: "superadmin", label: "Superadmin" },
                { value: "admin",      label: "Admin"      },
                { value: "sekretaris", label: "Sekretaris" },
                { value: "bendahara",  label: "Bendahara"  },
                { value: "user",       label: "User"       },
              ]}
            />
          </div>
          <div>
            <button type="submit" className="btn-primary w-full">
              Create User
            </button>
          </div>
        </form>
      </div>

      {/* ── Search ── */}
      <div className="relative max-w-md">
        <svg
          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={1.8}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
        </svg>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by username, role, or kelompok…"
          className="glass-input pl-10"
        />
      </div>

      {/* ── Users table ── */}
      <div className="glass-table-wrap">
        <table className="glass-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Role</th>
              <th>Kelompok</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={5} className="px-5 py-8 text-center text-muted">
                  Loading users…
                </td>
              </tr>
            )}
            {!loading && regularUsers.length === 0 && (
              <tr>
                <td colSpan={5} className="px-5 py-8 text-center text-muted">
                  No users found.
                </td>
              </tr>
            )}
            {!loading && regularUsers.length > 0 && filteredUsers.length === 0 && (
              <tr>
                <td colSpan={5} className="px-5 py-8 text-center text-muted">
                  No users match your search.
                </td>
              </tr>
            )}
            {!loading && filteredUsers.map((u) => {
              const isSelf = me && u.id === me.id;
              return (
                <tr key={u.id}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      {u.avatar_url ? (
                        <AvatarViewer
                          src={u.avatar_url}
                          alt={u.username}
                          className="w-8 h-8 rounded-full flex-shrink-0"
                          style={{ border: "2px solid var(--border)" }}
                        />
                      ) : (
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ background: "var(--bg-surface-hover)", border: "2px solid var(--border)" }}
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"
                            style={{ color: "var(--text-secondary)" }}>
                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v1h16v-1c0-2.66-5.33-4-8-4z"/>
                          </svg>
                        </div>
                      )}
                      <div>
                        <span className="font-medium text-[13px]" style={{ color: "var(--text-primary)" }}>
                          {u.username}
                        </span>
                        {isSelf && (
                          <em className="ml-2 text-[11px] not-italic font-semibold" style={{ color: "var(--accent)" }}>
                            (you)
                          </em>
                        )}
                        {u.google_linked && (
                          <span className="badge badge-info ml-2" title={u.google_email}>Google</span>
                        )}
                        {u.google_linked && u.google_email && (
                          <div className="text-[11px] break-all" style={{ color: "var(--text-secondary)" }}>
                            {u.google_email}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    {u.registration_status === "rejected" ? (
                      // Rejected sign-ups have no role and must not be given one from
                      // here (login would still say "ditolak"): delete to let them retry.
                      <span className="badge badge-danger">Ditolak</span>
                    ) : (
                    /* Invisible select overlaid on role badge */
                    <span className="relative inline-block">
                      <span className={`role-badge ${ROLE_CLASS[u.role] || "role-user"}`}>
                        {ROLE_LABEL[u.role] || u.role}
                      </span>
                      {!isSelf && (
                        <select
                          value={u.role}
                          onChange={(e) => handleChangeRole(u.id, e.target.value)}
                          className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                          title="Change role"
                        >
                          <option value="superadmin">Superadmin</option>
                          <option value="admin">Admin</option>
                          <option value="sekretaris">Sekretaris</option>
                          <option value="bendahara">Bendahara</option>
                          <option value="user">User</option>
                        </select>
                      )}
                    </span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <TeamBadgeList teams={u.teams || []} />
                  </td>
                  <td className="px-5 py-3">
                    <StatusDot active={u.is_active} />
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openResetModal(u)}
                        className="btn-outline btn-sm"
                      >
                        Reset Password
                      </button>
                      {u.google_linked && (
                        <button
                          onClick={() => handleUnlinkGoogle(u)}
                          disabled={u.has_password === false}
                          title={
                            u.has_password === false
                              ? "Akun ini belum punya password, jadi kalau Google-nya dilepas dia tidak bisa masuk. Atur password dulu lewat Reset Password."
                              : undefined
                          }
                          className="btn-outline btn-sm"
                        >
                          Lepas Google
                        </button>
                      )}
                      <button
                        onClick={() => handleToggleActive(u)}
                        disabled={isSelf}
                        title={isSelf ? "Kamu tidak bisa menangguhkan akun sendiri." : undefined}
                        className={u.is_active ? "btn-outline btn-sm" : "btn-primary btn-sm"}
                      >
                        {u.is_active ? "Suspend" : "Activate"}
                      </button>
                      <button
                        onClick={() => handleDeleteUser(u.id)}
                        disabled={isSelf}
                        title={isSelf ? "Kamu tidak bisa menghapus akun sendiri." : undefined}
                        className="btn-danger btn-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Reset password modal ── */}
      {showResetModal && resetUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)" }}>
          <div className="glass rounded-[24px] w-full max-w-sm p-6 animate-scale-in">
            <h2 className="text-[16px] font-semibold mb-1" style={{ color: "var(--text-primary)" }}>
              Reset Password
            </h2>
            <p className="text-[13px] mb-5" style={{ color: "var(--text-secondary)" }}>
              for <span className="font-semibold" style={{ color: "var(--text-primary)" }}>{resetUser.username}</span>
            </p>

            <form onSubmit={handleResetPasswordSubmit} className="space-y-3">
              <div>
                <label className="block text-[11px] font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>
                  New password *
                </label>
                <PasswordInput
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  className="glass-input"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>
                  Confirm new password *
                </label>
                <PasswordInput
                  value={resetConfirmPassword}
                  onChange={(e) => setResetConfirmPassword(e.target.value)}
                  className="glass-input"
                />
              </div>

              {resetError && (
                <div className="rounded-lg px-3 py-2 text-[12px] border"
                  style={{ color: "var(--danger)", background: "var(--danger-subtle)", borderColor: "color-mix(in srgb, var(--danger) 25%, transparent)" }}>
                  {resetError}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" className="btn-ghost btn-sm" onClick={closeResetModal} disabled={resetLoading}>
                  Cancel
                </button>
                <button type="submit" disabled={resetLoading} className="btn-primary btn-sm">
                  {resetLoading ? "Saving…" : "Save password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;
